import { useMemo } from "react";

import { useMaterials } from "#modules/materials/view/useMaterials";
import {
  buildSelectOptionValue,
  findMaterialFromSelectOptions,
  getMaterialSelectOptions,
} from "#modules/quoteRequestLines/domain/getMaterialSelectOptions";
import { QuoteRequestLineDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLine.api";
import { useMaterialChangeDisclaimerModal } from "#modules/quoteRequestLines/useMaterialChangeDisclaimerModal";
import { AssemblyWithChildren, MaterialGradeType, PartTypeSchema } from "shared";

const MIXED_MATERIALS_VALUE = "__MIXED_MATERIALS__";

type MaterialSelectValue = {
  materialName: string;
  materialGrade: MaterialGradeType | null;
};

type Props = {
  onChange: (materialName: string, materialGrade: MaterialGradeType | null) => void;
  assembly?: AssemblyWithChildren<QuoteRequestLineDto>;
  quoteRequestLine?: QuoteRequestLineDto;
  value?: MaterialSelectValue | null;
};

export const useMaterialSelect = ({ onChange, assembly, quoteRequestLine, value }: Props) => {
  const { materials } = useMaterials();
  const { modalOpened, handleConfirm, handleCancel, checkboxProps, showModalOrConfirm } =
    useMaterialChangeDisclaimerModal();

  const isControlled = value !== undefined;
  const currentMaterialName = isControlled
    ? (value?.materialName ?? "")
    : (quoteRequestLine?.materialName ?? "");
  const currentMaterialGrade = isControlled
    ? (value?.materialGrade ?? null)
    : (quoteRequestLine?.materialGrade ?? null);

  const hasMixedMaterials = useMemo(() => {
    if (!assembly || assembly.children.length === 0) {
      return false;
    }

    const parentMaterial = buildSelectOptionValue(
      assembly.quoteRequestLine.materialGrade,
      assembly.quoteRequestLine.materialName,
    );

    const allChildrenHasSameMaterialGrade = assembly.children.every((child) => {
      const childMaterial = buildSelectOptionValue(
        child.quoteRequestLine.materialGrade,
        child.quoteRequestLine.materialName,
      );

      return (
        child.quoteRequestLine.part.type === PartTypeSchema.enum.CONSUMABLE ||
        childMaterial === parentMaterial
      );
    });

    return !allChildrenHasSameMaterialGrade;
  }, [assembly]);

  const hasAssemblyChildren = useMemo(() => {
    return !!assembly?.children && assembly.children.length > 0;
  }, [assembly]);

  const displayValue = useMemo(() => {
    if (hasMixedMaterials) {
      return MIXED_MATERIALS_VALUE;
    }
    return buildSelectOptionValue(currentMaterialGrade, currentMaterialName) || null;
  }, [hasMixedMaterials, currentMaterialGrade, currentMaterialName]);

  const materialOptions = useMemo(() => {
    const options = getMaterialSelectOptions(materials, currentMaterialName, currentMaterialGrade);

    if (hasMixedMaterials) {
      return [{ label: "-", value: MIXED_MATERIALS_VALUE }, ...options];
    }

    return options;
  }, [materials, currentMaterialName, currentMaterialGrade, hasMixedMaterials]);

  const applyMaterialChange = (materialName: string, materialGrade: MaterialGradeType | null) => {
    onChange(materialName, materialGrade);
  };

  const onSelectChange = (selectedValue: string | null) => {
    if (!selectedValue || selectedValue === MIXED_MATERIALS_VALUE) {
      return;
    }

    const matchedMaterial = findMaterialFromSelectOptions(selectedValue, materials);

    showModalOrConfirm({
      materialName: matchedMaterial.materialName,
      materialGrade: matchedMaterial.materialGrade,
      onConfirm: applyMaterialChange,
      shouldShowModal: hasAssemblyChildren,
    });
  };

  const onModalConfirm = () => {
    handleConfirm((materialName, materialGrade) =>
      applyMaterialChange(materialName, materialGrade),
    );
  };

  return {
    displayValue,
    materialOptions,
    onSelectChange,
    modalOpened,
    onModalClose: handleCancel,
    onModalConfirm,
    checkboxProps,
  };
};
